// External imports
import React, { useEffect, useRef, useState } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import axios from 'axios';
// Internal imports
import Spinner from '#/Common/Spinner.jsx';
import { AUTH_STAGE, SPINNER_DIALOGS } from '~/app/constants.js';
import { webSocket } from '~/app/Utility/socketConnection.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import WebHelpers from '~/app/webHelpers.js';
// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { actions as appActions } from '~/app/redux/modules/app';
import AuthAPI from '~/app/api/authAPI.js';
import {
  processSSOHandoffResponse,
  startSSOHandoffExchange,
} from '~/app/Pages/SSOAccountPicker/helpers/ssoHandoffFlow';

/**
 * Cross-origin SSO token handoff screen.
 *
 * Exchanges a one-time server-issued handoff code for session tokens, stores them
 * under the current origin, and resumes the standard post-login route flow.
 *
 * @component
 * @category SSO
 * @see CLO-11566
 */
const SSOHandoff = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, isAuthenticating, authStage } = useSelector(state => ({
    isAuthenticated: state.app.isAuthenticated,
    isAuthenticating: state.loginSetup.isAuthenticating,
    authStage: state.app.authStage,
  }));
  const [isFinalizingLogin, setIsFinalizingLogin] = useState(false);
  // Tracks whether finalize has actually entered an auth-processing phase.
  // We only treat finalize as failed after seeing authenticating=true at least once.
  const sawFinalizeAuthCycle = useRef(false);

  /**
   * Redirects already authenticated users directly to dashboard.
   */
  useEffect(() => {
    if (isAuthenticated) {
      setIsFinalizingLogin(false);
      sawFinalizeAuthCycle.current = false;
      webSocket.services();
      history.push({ pathname: '/dashboard' });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isFinalizingLogin || isAuthenticated) {
      return;
    }

    // Observe one full finalize-login auth cycle. We only recover as failure
    // after auth processing starts and then returns to idle without success.
    if (isAuthenticating) {
      sawFinalizeAuthCycle.current = true;
      return;
    }

    if (!sawFinalizeAuthCycle.current) {
      return;
    }

    // onFinalizeLogin may intentionally transition to pick-office flow before
    // isAuthenticated flips true; treat that as success-path continuation.
    if (authStage === AUTH_STAGE.AWAIT_PICK_OFFICE) {
      setIsFinalizingLogin(false);
      sawFinalizeAuthCycle.current = false;
      history.push('/database');
      return;
    }

    // Finalization ended without establishing authenticated app state.
    // Fail closed by removing any partial session artifacts and restarting login.
    setIsFinalizingLogin(false);
    sawFinalizeAuthCycle.current = false;
    WebHelpers.cleanupSession();
    WebHelpers.cleanupLocalStorage();
    dispatch(appActions.onLogout());
    ErrorHelpers.handleError(
      'SSO Login Error',
      ErrorHelpers.createSimpleError('Unable to finalize sign in. Please sign in again.'),
    );
    history.replace('/');
  }, [isFinalizingLogin, isAuthenticated, isAuthenticating, authStage]);

  /**
   * Exchanges the handoff code for tokens and routes to the next post-login screen.
   */
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    // Route failures and malformed payloads through one fail-closed branch.
    // This keeps cleanup behavior identical regardless of where failure occurs.
    const handleHandoffFailure = () => {
      WebHelpers.cleanupSession();
      WebHelpers.cleanupLocalStorage();
      dispatch(appActions.onLogout());
      ErrorHelpers.handleError(
        'SSO Login Error',
        ErrorHelpers.createSimpleError('Your SSO handoff failed. Please sign in again.'),
      );
      history.replace('/');
    };

    return startSSOHandoffExchange({
      search: window.location.search,
      createCancelSource: () => axios.CancelToken.source(),
      isCancel: axios.isCancel,
      exchangeSSOHandoffCode: (code, requestConfig) =>
        AuthAPI.exchangeSSOHandoffCode(code, requestConfig),
      onMissingCode: () => {
        // Missing one-time code means callback URL is invalid or stale.
        // Fail closed and force a fresh login initiation.
        WebHelpers.cleanupSession();
        WebHelpers.cleanupLocalStorage();
        dispatch(appActions.onLogout());
        history.replace('/');
      },
      onProcessResponse: res => {
        try {
          // Branch routing (TOC/IRS/finalize) and token persistence are handled
          // by the shared helper so behavior stays consistent and testable.
          processSSOHandoffResponse({
            responseData: res.data,
            history,
            dispatch,
            setIsFinalizingLogin,
            sawFinalizeAuthCycleRef: sawFinalizeAuthCycle,
          });
        } catch (_) {
          handleHandoffFailure();
        }
      },
      onExchangeFailure: () => {
        handleHandoffFailure();
      },
    });
  }, []);

  return (
    <Spinner
      size={100}
      spinnerColor="blue"
      loadingText={SPINNER_DIALOGS.LOADING}
      textColor="white"
      bgColor="grey"
      lockActions={true}
      skipDelay
    />
  );
};

export default withRouter(SSOHandoff);
